import { supabase } from '../supabaseClient';
import {
  emptyGroupMemberPoints,
  type AssignmentPointKind,
  type AssignmentPointStatus,
  type CoinRecordType,
  type GroupMemberPoints,
  type ItemCardCategory,
  type MemberAssignmentPoint,
  type MemberCoinRecord,
  type MemberItemInstance,
  type MemberNoteRecord,
  type MemberNoteType,
  type MemberPointRecord,
  type MemberProfileCardUnlock,
  type PointItemNoteDoc,
  type PointTypeCategory,
} from '../types/pointItemNote';
import { getAssignmentPointsNote, saveAssignmentPointsNote } from './assignmentPointsNoteService';
import { deleteMemberPhoto } from './memberProfilePhotoStorageService';
import {
  loadPointItemNoteCache,
  savePointItemNoteCache,
} from '../utils/pointItemNoteStorage';
import { invalidateSchoolScopedDataCaches } from '../utils/schoolDataCacheVersion';

export type MemberOwnedDataSummary = {
  pointRecordCount: number;
  coinRecordCount: number;
  noteCount: number;
  itemInstanceCount: number;
  /** 과제 노트에 남은 배정·상태 soft reference */
  assignmentRefCount: number;
  /** class_member_assignment_rewards 행 */
  assignmentRewardCount: number;
  /** class_member_profile_cards 행 */
  profileCardCount: number;
  /** 업로드된 멤버 프로필 사진(storage) */
  photoProfileCount: number;
  /** 이관 전 JSON에만 남은 멤버 데이터 */
  legacyJsonHit: boolean;
};

export function memberOwnedDataHasContent(summary: MemberOwnedDataSummary): boolean {
  return (
    summary.pointRecordCount > 0 ||
    summary.coinRecordCount > 0 ||
    summary.noteCount > 0 ||
    summary.itemInstanceCount > 0 ||
    summary.assignmentRefCount > 0 ||
    summary.assignmentRewardCount > 0 ||
    summary.profileCardCount > 0 ||
    summary.photoProfileCount > 0 ||
    summary.legacyJsonHit
  );
}

async function countRows(
  table: string,
  memberIds: string[]
): Promise<number> {
  if (memberIds.length === 0) return 0;
  const { count, error } = await supabase
    .from(table)
    .select('id', { count: 'exact', head: true })
    .in('class_member_id', memberIds);
  if (error) {
    if (/does not exist|schema cache/i.test(error.message ?? '')) return 0;
    throw new Error(error.message);
  }
  return count ?? 0;
}

function countAssignmentRefs(
  items: Array<{
    assignedMemberIds?: string[];
    studentStatuses?: Array<{ studentId: string }>;
    studentStatusesBySubTask?: Record<string, { studentId?: string }>;
    dateStatuses?: Array<{ studentStatuses?: Array<{ studentId: string }> }>;
  }>,
  memberIdSet: Set<string>
): number {
  let n = 0;
  for (const item of items) {
    const assigned = item.assignedMemberIds ?? [];
    if (assigned.some((id) => memberIdSet.has(id))) {
      n += 1;
      continue;
    }
    if ((item.studentStatuses ?? []).some((s) => memberIdSet.has(s.studentId))) {
      n += 1;
      continue;
    }
    if (
      item.studentStatusesBySubTask &&
      Object.values(item.studentStatusesBySubTask).some(
        (s) => s.studentId && memberIdSet.has(s.studentId)
      )
    ) {
      n += 1;
      continue;
    }
    if (
      (item.dateStatuses ?? []).some((bucket) =>
        (bucket.studentStatuses ?? []).some((s) => memberIdSet.has(s.studentId))
      )
    ) {
      n += 1;
    }
  }
  return n;
}

async function hasLegacyJsonMemberData(
  workspaceId: string,
  gradeId: string,
  memberIds: string[]
): Promise<boolean> {
  if (!memberIds.length) return false;
  const idSet = new Set(memberIds);
  const cached = loadPointItemNoteCache(workspaceId, gradeId);
  if (cached) {
    for (const g of Object.values(cached.memberPointsByGroup)) {
      if (
        g.records.some((r) => idSet.has(r.memberId)) ||
        g.coinRecords.some((r) => idSet.has(r.memberId)) ||
        g.notes.some((r) => idSet.has(r.memberId)) ||
        (g.itemInstances ?? []).some((r) => idSet.has(r.memberId)) ||
        (g.profileCardUnlocks ?? []).some((r) => idSet.has(r.memberId)) ||
        Object.keys(g.totals).some((id) => idSet.has(id) && (g.totals[id] ?? 0) !== 0) ||
        Object.keys(g.coinTotals).some((id) => idSet.has(id) && (g.coinTotals[id] ?? 0) !== 0)
      ) {
        return true;
      }
    }
  }
  const { data, error } = await supabase
    .from('point_item_notes')
    .select('payload')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId)
    .maybeSingle();
  if (error || !data) return false;
  const payload = data.payload as { memberPointsByGroup?: Record<string, GroupMemberPoints> };
  const byGroup = payload?.memberPointsByGroup;
  if (!byGroup || typeof byGroup !== 'object') return false;
  for (const g of Object.values(byGroup)) {
    if (!g || typeof g !== 'object') continue;
    const records = Array.isArray(g.records) ? g.records : [];
    const coinRecords = Array.isArray(g.coinRecords) ? g.coinRecords : [];
    const notes = Array.isArray(g.notes) ? g.notes : [];
    const itemInstances = Array.isArray(g.itemInstances) ? g.itemInstances : [];
    const profileCardUnlocks = Array.isArray(g.profileCardUnlocks) ? g.profileCardUnlocks : [];
    if (
      records.some((r) => r && idSet.has(String((r as { memberId?: string }).memberId))) ||
      coinRecords.some((r) => r && idSet.has(String((r as { memberId?: string }).memberId))) ||
      notes.some((r) => r && idSet.has(String((r as { memberId?: string }).memberId))) ||
      itemInstances.some((r) => r && idSet.has(String((r as { memberId?: string }).memberId))) ||
      profileCardUnlocks.some((r) => r && idSet.has(String((r as { memberId?: string }).memberId)))
    ) {
      return true;
    }
  }
  return false;
}

/** 멤버 삭제 전: 포인트·코인·메모·아이템·과제·프로필카드·사진 등 소유 여부 */
export async function getMemberOwnedDataSummary(
  workspaceId: string,
  gradeId: string,
  memberIds: string[]
): Promise<MemberOwnedDataSummary> {
  const ids = Array.from(new Set(memberIds.filter(Boolean)));
  const empty: MemberOwnedDataSummary = {
    pointRecordCount: 0,
    coinRecordCount: 0,
    noteCount: 0,
    itemInstanceCount: 0,
    assignmentRefCount: 0,
    assignmentRewardCount: 0,
    profileCardCount: 0,
    photoProfileCount: 0,
    legacyJsonHit: false,
  };
  if (!workspaceId || !gradeId || ids.length === 0) return empty;

  const [
    pointRecordCount,
    coinRecordCount,
    noteCount,
    itemInstanceCount,
    assignmentRewardCount,
    profileCardCount,
    photoProfileCount,
    assignmentDoc,
    legacyJsonHit,
  ] = await Promise.all([
    countRows('class_member_point_records', ids),
    countRows('class_member_coin_records', ids),
    countRows('class_member_notes', ids),
    countRows('class_member_item_instances', ids),
    countRows('class_member_assignment_rewards', ids),
    countRows('class_member_profile_cards', ids),
    countMemberPhotoProfiles(ids),
    getAssignmentPointsNote(workspaceId, gradeId),
    hasLegacyJsonMemberData(workspaceId, gradeId, ids),
  ]);

  const assignmentRefCount = countAssignmentRefs(assignmentDoc.items, new Set(ids));

  return {
    pointRecordCount,
    coinRecordCount,
    noteCount,
    itemInstanceCount,
    assignmentRefCount,
    assignmentRewardCount,
    profileCardCount,
    photoProfileCount,
    legacyJsonHit,
  };
}

async function countMemberPhotoProfiles(memberIds: string[]): Promise<number> {
  if (memberIds.length === 0) return 0;
  const { count, error } = await supabase
    .from('class_members')
    .select('id', { count: 'exact', head: true })
    .in('id', memberIds)
    .not('photo_profile_path', 'is', null)
    .neq('photo_profile_path', '');
  if (error) {
    if (/does not exist|schema cache|photo_profile/i.test(error.message ?? '')) return 0;
    throw new Error(error.message);
  }
  return count ?? 0;
}

function stripMemberFromAssignmentItems<T extends { assignedMemberIds: string[] }>(
  items: T[],
  memberIdSet: Set<string>
): T[] {
  return items.map((item) => {
    const assignedMemberIds = item.assignedMemberIds.filter((id) => !memberIdSet.has(id));
    const next = { ...item, assignedMemberIds } as T & {
      studentStatuses?: Array<{ studentId: string }>;
      studentStatusesBySubTask?: Record<string, { studentId?: string }>;
      dateStatuses?: Array<{ studentStatuses: Array<{ studentId: string }> }>;
    };
    if (Array.isArray(next.studentStatuses)) {
      next.studentStatuses = next.studentStatuses.filter((s) => !memberIdSet.has(s.studentId));
    }
    if (next.studentStatusesBySubTask && typeof next.studentStatusesBySubTask === 'object') {
      const map: Record<string, { studentId?: string }> = {};
      Object.entries(next.studentStatusesBySubTask).forEach(([k, v]) => {
        const sid = v?.studentId ?? k.split(':').slice(1).join(':');
        if (sid && memberIdSet.has(sid)) return;
        map[k] = v;
      });
      next.studentStatusesBySubTask = map;
    }
    if (Array.isArray(next.dateStatuses)) {
      next.dateStatuses = next.dateStatuses.map((bucket) => ({
        ...bucket,
        studentStatuses: (bucket.studentStatuses ?? []).filter((s) => !memberIdSet.has(s.studentId)),
      }));
    }
    return next;
  });
}

/** 과제 노트 payload에서 멤버 soft reference 제거 */
export async function purgeMemberRefsFromAssignmentNotes(
  workspaceId: string,
  gradeId: string,
  memberIds: string[]
): Promise<void> {
  const ids = Array.from(new Set(memberIds.filter(Boolean)));
  if (!workspaceId || !gradeId || ids.length === 0) return;
  const doc = await getAssignmentPointsNote(workspaceId, gradeId);
  const idSet = new Set(ids);
  if (countAssignmentRefs(doc.items, idSet) === 0) return;
  await saveAssignmentPointsNote({
    ...doc,
    items: stripMemberFromAssignmentItems(doc.items, idSet),
    updatedAt: new Date().toISOString(),
  });
}

/** 로컬 캐시·레거시 JSON에서 멤버 포인트 데이터 제거 */
export async function purgeMemberRefsFromPointItemNoteCache(
  workspaceId: string,
  gradeId: string,
  memberIds: string[]
): Promise<void> {
  const ids = Array.from(new Set(memberIds.filter(Boolean)));
  if (!workspaceId || !gradeId || ids.length === 0) return;
  const idSet = new Set(ids);
  const cached = loadPointItemNoteCache(workspaceId, gradeId);
  if (cached) {
    const nextByGroup: Record<string, GroupMemberPoints> = {};
    Object.entries(cached.memberPointsByGroup).forEach(([gid, g]) => {
      const totals = { ...g.totals };
      const coinTotals = { ...g.coinTotals };
      const assignmentTotals = { ...(g.assignmentTotals ?? {}) };
      ids.forEach((id) => {
        delete totals[id];
        delete coinTotals[id];
        delete assignmentTotals[id];
      });
      nextByGroup[gid] = {
        ...g,
        totals,
        coinTotals,
        assignmentTotals,
        records: (g.records ?? []).filter((r) => !idSet.has(r.memberId)),
        coinRecords: (g.coinRecords ?? []).filter((r) => !idSet.has(r.memberId)),
        notes: (g.notes ?? []).filter((r) => !idSet.has(r.memberId)),
        itemInstances: (g.itemInstances ?? []).filter((r) => !idSet.has(r.memberId)),
        profileCardUnlocks: (g.profileCardUnlocks ?? []).filter((r) => !idSet.has(r.memberId)),
      };
    });
    savePointItemNoteCache({ ...cached, memberPointsByGroup: nextByGroup });
  }

  const { data, error } = await supabase
    .from('point_item_notes')
    .select('id, payload, updated_at')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId)
    .maybeSingle();
  if (error || !data) return;
  const payload = { ...(data.payload as Record<string, unknown>) };
  if (!payload.memberPointsByGroup || typeof payload.memberPointsByGroup !== 'object') return;
  const byGroup = payload.memberPointsByGroup as Record<string, GroupMemberPoints>;
  const nextByGroup: Record<string, GroupMemberPoints> = {};
  let changed = false;
  Object.entries(byGroup).forEach(([gid, g]) => {
    if (!g || typeof g !== 'object') {
      nextByGroup[gid] = g;
      return;
    }
    const records = Array.isArray(g.records)
      ? g.records.filter((r) => r && !idSet.has(String((r as { memberId?: string }).memberId)))
      : [];
    const coinRecords = Array.isArray(g.coinRecords)
      ? g.coinRecords.filter((r) => r && !idSet.has(String((r as { memberId?: string }).memberId)))
      : [];
    const notes = Array.isArray(g.notes)
      ? g.notes.filter((r) => r && !idSet.has(String((r as { memberId?: string }).memberId)))
      : [];
    const itemInstances = Array.isArray(g.itemInstances)
      ? g.itemInstances.filter((r) => r && !idSet.has(String((r as { memberId?: string }).memberId)))
      : [];
    const profileCardUnlocks = Array.isArray(g.profileCardUnlocks)
      ? g.profileCardUnlocks.filter(
          (r) => r && !idSet.has(String((r as { memberId?: string }).memberId))
        )
      : [];
    const totals = { ...(g.totals ?? {}) };
    const coinTotals = { ...(g.coinTotals ?? {}) };
    const assignmentTotals = { ...(g.assignmentTotals ?? {}) };
    ids.forEach((id) => {
      if (id in totals || id in coinTotals || id in assignmentTotals) changed = true;
      delete totals[id];
      delete coinTotals[id];
      delete assignmentTotals[id];
    });
    if (
      records.length !== (g.records?.length ?? 0) ||
      coinRecords.length !== (g.coinRecords?.length ?? 0) ||
      notes.length !== (g.notes?.length ?? 0) ||
      itemInstances.length !== (g.itemInstances?.length ?? 0) ||
      profileCardUnlocks.length !== (g.profileCardUnlocks?.length ?? 0)
    ) {
      changed = true;
    }
    nextByGroup[gid] = {
      ...g,
      totals,
      coinTotals,
      assignmentTotals,
      records,
      coinRecords,
      notes,
      itemInstances,
      profileCardUnlocks,
    } as GroupMemberPoints;
  });
  if (!changed) return;
  const updatedAt = new Date().toISOString();
  await supabase
    .from('point_item_notes')
    .update({
      payload: { ...payload, memberPointsByGroup: nextByGroup, updatedAt },
      updated_at: updatedAt,
    })
    .eq('id', data.id);
}

/** 멤버 프로필 사진(storage + 할당량 메타) 삭제 — class_members 행 삭제 전에 호출 */
export async function purgeMemberProfilePhotos(
  workspaceId: string,
  gradeId: string,
  memberIds: string[]
): Promise<void> {
  const ids = Array.from(new Set(memberIds.filter(Boolean)));
  if (!workspaceId || !gradeId || ids.length === 0) return;

  const { data, error } = await supabase
    .from('class_members')
    .select('id, photo_profile_path')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId)
    .in('id', ids);
  if (error) {
    if (/does not exist|schema cache|photo_profile/i.test(error.message ?? '')) return;
    throw new Error(error.message);
  }

  await Promise.all(
    ((data ?? []) as Array<{ id?: string; photo_profile_path?: string | null }>).map((row) => {
      const memberId = typeof row.id === 'string' ? row.id : '';
      if (!memberId) return Promise.resolve();
      const path = (row.photo_profile_path ?? '').trim();
      return deleteMemberPhoto({
        workspaceId,
        gradeId,
        memberId,
        storagePath: path || null,
      }).catch(() => undefined);
    })
  );
}

export async function purgeMemberOwnedSoftRefs(
  workspaceId: string,
  gradeId: string,
  memberIds: string[]
): Promise<void> {
  await Promise.all([
    purgeMemberRefsFromAssignmentNotes(workspaceId, gradeId, memberIds),
    purgeMemberRefsFromPointItemNoteCache(workspaceId, gradeId, memberIds),
    purgeMemberProfilePhotos(workspaceId, gradeId, memberIds),
  ]);
  if (memberIds.length > 0) {
    await invalidateSchoolScopedDataCaches(workspaceId);
  }
}

function recomputeTotals(records: MemberPointRecord[]): Record<string, number> {
  const totals: Record<string, number> = {};
  records.forEach((r) => {
    totals[r.memberId] = (totals[r.memberId] ?? 0) + r.pointValue;
  });
  return totals;
}

export function recomputeCoinTotals(records: MemberCoinRecord[]): Record<string, number> {
  const totals: Record<string, number> = {};
  records.forEach((r) => {
    totals[r.memberId] = (totals[r.memberId] ?? 0) + r.coinValue;
  });
  return totals;
}

/** 코인 표시용: coinRecords 합산 (coinTotals 맵 drift 방지) */
export function memberDisplayCoinTotal(
  group: GroupMemberPoints,
  memberId: string
): number {
  let sum = 0;
  for (const r of group.coinRecords ?? []) {
    if (r.memberId === memberId) sum += r.coinValue;
  }
  return sum;
}

function mapPointRow(row: Record<string, unknown>): MemberPointRecord | null {
  const id = typeof row.id === 'string' ? row.id : '';
  const memberId = typeof row.class_member_id === 'string' ? row.class_member_id : '';
  if (!id || !memberId) return null;
  return {
    id,
    memberId,
    pointTypeId: typeof row.point_type_id === 'string' ? row.point_type_id : '',
    pointValue: Number(row.point_value) || 0,
    pointMemo: typeof row.point_memo === 'string' ? row.point_memo : '',
    pointCategory: (row.point_category === 'negative' ? 'negative' : 'positive') as PointTypeCategory,
    recordedDate: typeof row.recorded_date === 'string' ? row.recorded_date : '',
    timeTagLabel: typeof row.time_tag_label === 'string' ? row.time_tag_label : undefined,
    createdAt:
      typeof row.created_at === 'string' ? row.created_at : new Date().toISOString(),
  };
}

function mapCoinType(raw: unknown): CoinRecordType {
  if (raw === 'Levelup' || raw === 'Store' || raw === 'Assignment') return raw;
  return 'General';
}

function mapCoinRow(row: Record<string, unknown>): MemberCoinRecord | null {
  const id = typeof row.id === 'string' ? row.id : '';
  const memberId = typeof row.class_member_id === 'string' ? row.class_member_id : '';
  if (!id || !memberId) return null;
  return {
    id,
    memberId,
    coinValue: Number(row.coin_value) || 0,
    coinType: mapCoinType(row.coin_type),
    memo: typeof row.memo === 'string' ? row.memo : undefined,
    recordedDate: typeof row.recorded_date === 'string' ? row.recorded_date : '',
    timeTagLabel: typeof row.time_tag_label === 'string' ? row.time_tag_label : undefined,
    createdAt:
      typeof row.created_at === 'string' ? row.created_at : new Date().toISOString(),
  };
}

function mapUsageHistory(raw: unknown): MemberItemInstance['usageHistory'] {
  if (!Array.isArray(raw)) return undefined;
  const list = raw
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const o = entry as Record<string, unknown>;
      const usedAt =
        typeof o.usedAt === 'string'
          ? o.usedAt
          : o.used_at != null
            ? new Date(o.used_at as string).toISOString()
            : o.usedAt != null
              ? new Date(o.usedAt as string).toISOString()
              : '';
      if (!usedAt || Number.isNaN(Date.parse(usedAt))) return null;
      const memo =
        typeof o.memo === 'string' && o.memo.trim() ? o.memo.trim() : undefined;
      return memo ? { usedAt, memo } : { usedAt };
    })
    .filter((x): x is NonNullable<typeof x> => x != null);
  return list.length > 0 ? list : undefined;
}

function mapItemRow(row: Record<string, unknown>): MemberItemInstance | null {
  const id = typeof row.id === 'string' ? row.id : '';
  const memberId = typeof row.class_member_id === 'string' ? row.class_member_id : '';
  const itemCardId = typeof row.item_card_id === 'string' ? row.item_card_id : '';
  if (!id || !memberId || !itemCardId) return null;
  const cat = row.category;
  const category: ItemCardCategory =
    cat === 'assignment' || cat === 'store' || cat === 'levelup' || cat === 'classPoint'
      ? cat
      : 'levelup';
  const remainingRaw = row.remaining_use_count;
  const remainingUseCount =
    typeof remainingRaw === 'number'
      ? remainingRaw
      : remainingRaw != null && remainingRaw !== ''
        ? Number(remainingRaw)
        : undefined;
  return {
    id,
    memberId,
    itemCardId,
    category,
    acquiredLevel:
      typeof row.acquired_level === 'number'
        ? row.acquired_level
        : row.acquired_level != null
          ? Number(row.acquired_level) || undefined
          : undefined,
    remainingUseCount: Number.isFinite(remainingUseCount as number)
      ? (remainingUseCount as number)
      : undefined,
    usageHistory: mapUsageHistory(row.usage_history),
    isLocked: row.is_locked === true,
    acquiredAt:
      typeof row.acquired_at === 'string' ? row.acquired_at : new Date().toISOString(),
  };
}

function mapProfileCardRow(row: Record<string, unknown>): MemberProfileCardUnlock | null {
  const id = typeof row.id === 'string' ? row.id : '';
  const memberId = typeof row.class_member_id === 'string' ? row.class_member_id : '';
  const setId = typeof row.set_id === 'string' ? row.set_id : '';
  if (!id || !memberId || !setId) return null;
  const sourceKind = row.source_kind === 'workspace' ? 'workspace' : 'admin';
  return {
    id,
    memberId,
    setId,
    slotIndex: Number(row.slot_index) || 0,
    sourceKind,
    quantity: Math.max(1, Number(row.quantity) || 1),
    unlockedAt: typeof row.unlocked_at === 'string' ? row.unlocked_at : new Date().toISOString(),
  };
}

/**
 * 특정 멤버의 프로필 카드 보유 이력을 DB에서 곧바로(캐시 우회) 조회한다.
 * 멤버 조회 모달을 열 때처럼, 다른 경로(뽑기 등)로 생긴 최신 변경을 즉시 반영해야 하는 곳에서 사용.
 */
export async function fetchMemberProfileCardUnlocksFresh(
  workspaceId: string,
  gradeId: string,
  memberId: string
): Promise<MemberProfileCardUnlock[]> {
  if (!workspaceId || !gradeId || !memberId) return [];
  const { data, error } = await supabase
    .from('class_member_profile_cards')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId)
    .eq('class_member_id', memberId)
    .order('unlocked_at', { ascending: false });
  if (error) {
    if (/does not exist|schema cache/i.test(error.message ?? '')) return [];
    throw new Error(error.message);
  }
  return ((data ?? []) as Record<string, unknown>[])
    .map(mapProfileCardRow)
    .filter((r): r is MemberProfileCardUnlock => r != null);
}

function mapNoteRow(row: Record<string, unknown>): MemberNoteRecord | null {
  const id = typeof row.id === 'string' ? row.id : '';
  const memberId = typeof row.class_member_id === 'string' ? row.class_member_id : '';
  if (!id || !memberId) return null;
  const noteTypeRaw = row.note_type;
  const noteType: MemberNoteType =
    noteTypeRaw === '학습' || noteTypeRaw === '행동' ? noteTypeRaw : '일반';
  return {
    id,
    memberId,
    text: typeof row.text === 'string' ? row.text : '',
    noteType,
    recordedDate: typeof row.recorded_date === 'string' ? row.recorded_date : '',
    timeTagLabel: typeof row.time_tag_label === 'string' ? row.time_tag_label : undefined,
    createdAt:
      typeof row.created_at === 'string' ? row.created_at : new Date().toISOString(),
  };
}

/**
 * class_member_notes.text는 암호화 저장되어 직접 select로는 복호화되지 않는다.
 * class_member_notes_list_by_grade / _by_members RPC(SECURITY DEFINER)로만 조회한다.
 */
async function fetchNotesByGrade(
  workspaceId: string,
  gradeId: string
): Promise<Record<string, unknown>[]> {
  const { data, error } = await supabase.rpc('class_member_notes_list_by_grade', {
    p_workspace_id: workspaceId,
    p_grade_id: gradeId,
  });
  if (error) {
    if (/does not exist|schema cache/i.test(error.message ?? '')) return [];
    throw new Error(error.message);
  }
  return (data ?? []) as Record<string, unknown>[];
}

async function fetchNotesByMembers(
  workspaceId: string,
  gradeId: string,
  memberIds: string[]
): Promise<Record<string, unknown>[]> {
  if (memberIds.length === 0) return [];
  const { data, error } = await supabase.rpc('class_member_notes_list_by_members', {
    p_workspace_id: workspaceId,
    p_grade_id: gradeId,
    p_member_ids: memberIds,
  });
  if (error) {
    if (/does not exist|schema cache/i.test(error.message ?? '')) return [];
    throw new Error(error.message);
  }
  return (data ?? []) as Record<string, unknown>[];
}

/** 기록 모아보기용: 학년 전체 멤버 기록(일반/학습/행동)만 로드 */
export async function loadClassMemberNotesForGrade(
  workspaceId: string,
  gradeId: string
): Promise<MemberNoteRecord[]> {
  if (!workspaceId || !gradeId) return [];
  const data = await fetchNotesByGrade(workspaceId, gradeId);
  return data.map(mapNoteRow).filter((r): r is MemberNoteRecord => r != null);
}

/** 학년 멤버 이력을 그룹별 UI 구조로 조립 */
export async function loadMemberPointsByGroup(
  workspaceId: string,
  gradeId: string
): Promise<Record<string, GroupMemberPoints>> {
  if (!workspaceId || !gradeId) return {};

  const [membersRes, pointsRes, coinsRes, notesRows, itemsRes, asgnRes, profileCardsRes] = await Promise.all([
    supabase
      .from('class_members')
      .select('id, member_group_id')
      .eq('workspace_id', workspaceId)
      .eq('grade_id', gradeId),
    supabase
      .from('class_member_point_records')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('grade_id', gradeId)
      .order('created_at', { ascending: false }),
    supabase
      .from('class_member_coin_records')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('grade_id', gradeId)
      .order('created_at', { ascending: false }),
    fetchNotesByGrade(workspaceId, gradeId),
    supabase
      .from('class_member_item_instances')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('grade_id', gradeId)
      .order('acquired_at', { ascending: false }),
    supabase
      .from('class_member_assignment_rewards')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('grade_id', gradeId)
      .order('recorded_date', { ascending: false }),
    supabase
      .from('class_member_profile_cards')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('grade_id', gradeId)
      .order('unlocked_at', { ascending: false }),
  ]);

  const tablesMissing =
    [pointsRes.error, coinsRes.error].some(
      (e) => e && /does not exist|schema cache/i.test(e.message ?? '')
    );

  if (tablesMissing) {
    return {};
  }
  if (membersRes.error) throw new Error(membersRes.error.message);
  if (pointsRes.error) throw new Error(pointsRes.error.message);
  if (coinsRes.error) throw new Error(coinsRes.error.message);
  // item/assignment 테이블 미적용 환경은 빈 목록으로 진행
  const itemsOk = !itemsRes.error || !/does not exist|schema cache/i.test(itemsRes.error.message ?? '');
  const asgnOk = !asgnRes.error || !/does not exist|schema cache/i.test(asgnRes.error.message ?? '');

  const memberGroup = new Map<string, string>();
  ((membersRes.data ?? []) as Array<{ id: string; member_group_id: string | null }>).forEach((m) => {
    if (m.member_group_id) memberGroup.set(m.id, m.member_group_id);
  });

  const byGroup: Record<string, GroupMemberPoints> = {};
  const ensure = (gid: string) => {
    if (!byGroup[gid]) byGroup[gid] = emptyGroupMemberPoints();
    return byGroup[gid];
  };

  const points = ((pointsRes.data ?? []) as Record<string, unknown>[])
    .map(mapPointRow)
    .filter((r): r is MemberPointRecord => r != null);
  const coins = ((coinsRes.data ?? []) as Record<string, unknown>[])
    .map(mapCoinRow)
    .filter((r): r is MemberCoinRecord => r != null);
  const notes = notesRows.map(mapNoteRow).filter((r): r is MemberNoteRecord => r != null);
  const items = itemsOk
    ? ((itemsRes.data ?? []) as Record<string, unknown>[])
        .map(mapItemRow)
        .filter((r): r is MemberItemInstance => r != null)
    : [];
  const asgnPoints = asgnOk
    ? ((asgnRes.data ?? []) as Record<string, unknown>[])
        .map(mapAssignmentPointRow)
        .filter((r): r is MemberAssignmentPoint => r != null)
    : [];
  if (
    profileCardsRes.error &&
    !/does not exist|schema cache/i.test(profileCardsRes.error.message ?? '')
  ) {
    throw new Error(profileCardsRes.error.message);
  }
  const profileCards = !profileCardsRes.error
    ? ((profileCardsRes.data ?? []) as Record<string, unknown>[])
        .map(mapProfileCardRow)
        .filter((r): r is MemberProfileCardUnlock => r != null)
    : [];

  points.forEach((r) => {
    const gid = memberGroup.get(r.memberId);
    if (!gid) return;
    ensure(gid).records.push(r);
  });
  coins.forEach((r) => {
    const gid = memberGroup.get(r.memberId);
    if (!gid) return;
    ensure(gid).coinRecords.push(r);
  });
  notes.forEach((r) => {
    const gid = memberGroup.get(r.memberId);
    if (!gid) return;
    ensure(gid).notes.push(r);
  });
  items.forEach((r) => {
    const gid = memberGroup.get(r.memberId);
    if (!gid) return;
    const g = ensure(gid);
    g.itemInstances = [...(g.itemInstances ?? []), r];
  });
  asgnPoints.forEach((r) => {
    // 학생 블럭 소속 그룹 우선 (이력 row.group_id 불일치 보정)
    const gid = memberGroup.get(r.memberId) || r.groupId;
    if (!gid) return;
    const g = ensure(gid);
    const normalized = r.groupId === gid ? r : { ...r, groupId: gid };
    g.assignmentPoints = [...(g.assignmentPoints ?? []), normalized];
  });
  profileCards.forEach((r) => {
    const gid = memberGroup.get(r.memberId);
    if (!gid) return;
    const g = ensure(gid);
    g.profileCardUnlocks = [...(g.profileCardUnlocks ?? []), r];
  });

  Object.values(byGroup).forEach((g) => {
    const behaviorTotals = recomputeTotals(g.records);
    const asgnTotals = recomputeAssignmentTotals(g.assignmentPoints ?? []);
    // totals = 행동 포인트 + 과제 포인트 (레벨업 기준)
    const combined: Record<string, number> = { ...behaviorTotals };
    Object.entries(asgnTotals).forEach(([mid, pts]) => {
      combined[mid] = (combined[mid] ?? 0) + pts;
    });
    g.totals = combined;
    g.coinTotals = recomputeCoinTotals(g.coinRecords);
    g.assignmentTotals = asgnTotals;
    if (!g.itemInstances) g.itemInstances = [];
    if (!g.assignmentPoints) g.assignmentPoints = [];
    if (!g.rewardedLevels) g.rewardedLevels = {};
    if (!g.profileCardUnlocks) g.profileCardUnlocks = [];
  });

  return byGroup;
}

async function listMemberIdsForGroup(
  workspaceId: string,
  gradeId: string,
  groupId: string
): Promise<string[]> {
  const { data: members, error: memErr } = await supabase
    .from('class_members')
    .select('id')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId)
    .eq('member_group_id', groupId);
  if (memErr) {
    if (/does not exist|schema cache/i.test(memErr.message ?? '')) return [];
    throw new Error(memErr.message);
  }
  return ((members ?? []) as Array<{ id: string }>).map((m) => m.id).filter(Boolean);
}

export type LoadMemberPointsForGroupOptions = {
  /** true면 매핑에 쓰는 컬럼만 select (프레젠 워밍용) */
  slimColumns?: boolean;
  /** false면 notes 테이블 조회 생략 */
  includeNotes?: boolean;
};

const POINT_RECORD_SLIM_COLS =
  'id, class_member_id, point_type_id, point_value, point_memo, point_category, recorded_date, time_tag_label, created_at';
const COIN_RECORD_SLIM_COLS =
  'id, class_member_id, coin_value, coin_type, memo, recorded_date, time_tag_label, created_at';
const ITEM_INSTANCE_SLIM_COLS =
  'id, class_member_id, item_card_id, category, acquired_level, remaining_use_count, usage_history, is_locked, acquired_at';
const ASSIGNMENT_REWARD_SLIM_COLS =
  'id, class_member_id, group_id, assignment_item_id, assignment_sub_id, assignment_kind, assignment_title, status, earned_points, earned_coins, earned_item_ids, recorded_date, memo, created_at';

/**
 * 그룹 단위로 포인트·코인·노트·아이템·과제보상 로드.
 * @param memberIds 화면 레이아웃 멤버 ID(권장). 없으면 DB member_group_id로 조회.
 */
export async function loadMemberPointsForGroup(
  workspaceId: string,
  gradeId: string,
  groupId: string,
  memberIds?: string[],
  options?: LoadMemberPointsForGroupOptions
): Promise<GroupMemberPoints> {
  const empty = emptyGroupMemberPoints();
  if (!workspaceId || !gradeId || !groupId) return empty;

  const ids =
    memberIds && memberIds.length > 0
      ? Array.from(new Set(memberIds.filter(Boolean)))
      : await listMemberIdsForGroup(workspaceId, gradeId, groupId);
  if (ids.length === 0) return empty;

  const slim = options?.slimColumns === true;
  const includeNotes = options?.includeNotes !== false;
  const pointCols = slim ? POINT_RECORD_SLIM_COLS : '*';
  const coinCols = slim ? COIN_RECORD_SLIM_COLS : '*';
  const itemCols = slim ? ITEM_INSTANCE_SLIM_COLS : '*';
  const asgnCols = slim ? ASSIGNMENT_REWARD_SLIM_COLS : '*';

  /* slim 컬럼 문자열은 런타임에만 쓰고, 타입은 '*'로 고정(supabase 파서 제약) */
  const [pointsRes, coinsRes, notesRows, itemsRes, asgnRes, profileCardsRes] = await Promise.all([
    supabase
      .from('class_member_point_records')
      .select(pointCols as '*')
      .eq('workspace_id', workspaceId)
      .eq('grade_id', gradeId)
      .in('class_member_id', ids)
      .order('created_at', { ascending: false }),
    supabase
      .from('class_member_coin_records')
      .select(coinCols as '*')
      .eq('workspace_id', workspaceId)
      .eq('grade_id', gradeId)
      .in('class_member_id', ids)
      .order('created_at', { ascending: false }),
    includeNotes ? fetchNotesByMembers(workspaceId, gradeId, ids) : Promise.resolve([]),
    supabase
      .from('class_member_item_instances')
      .select(itemCols as '*')
      .eq('workspace_id', workspaceId)
      .eq('grade_id', gradeId)
      .in('class_member_id', ids)
      .order('acquired_at', { ascending: false }),
    supabase
      .from('class_member_assignment_rewards')
      .select(asgnCols as '*')
      .eq('workspace_id', workspaceId)
      .eq('grade_id', gradeId)
      .in('class_member_id', ids)
      .order('recorded_date', { ascending: false }),
    supabase
      .from('class_member_profile_cards')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('grade_id', gradeId)
      .in('class_member_id', ids)
      .order('unlocked_at', { ascending: false }),
  ]);

  const isMissing = (e: { message?: string } | null) =>
    !!e && /does not exist|schema cache/i.test(e.message ?? '');

  // 일부 테이블만 없어도 나머지를 버리지 않음
  // (point_records 미적용인데 assignment_rewards만 있는 경우 과제 합산이 0이 되던 버그)
  const pointsOk = !pointsRes.error || isMissing(pointsRes.error);
  const coinsOk = !coinsRes.error || isMissing(coinsRes.error);
  if (pointsRes.error && !isMissing(pointsRes.error)) throw new Error(pointsRes.error.message);
  if (coinsRes.error && !isMissing(coinsRes.error)) throw new Error(coinsRes.error.message);

  const itemsOk =
    !itemsRes.error || isMissing(itemsRes.error) || /is_locked/i.test(itemsRes.error.message ?? '');
  if (itemsRes.error && !itemsOk) {
    throw new Error(itemsRes.error.message);
  }
  if (asgnRes.error && !isMissing(asgnRes.error)) {
    throw new Error(asgnRes.error.message);
  }
  const asgnOk = !asgnRes.error;

  const records =
    pointsOk && !pointsRes.error
      ? ((pointsRes.data ?? []) as Record<string, unknown>[])
          .map(mapPointRow)
          .filter((r): r is MemberPointRecord => r != null)
      : [];
  const coinRecords =
    coinsOk && !coinsRes.error
      ? ((coinsRes.data ?? []) as Record<string, unknown>[])
          .map(mapCoinRow)
          .filter((r): r is MemberCoinRecord => r != null)
      : [];
  const notes = notesRows.map(mapNoteRow).filter((r): r is MemberNoteRecord => r != null);
  const itemInstances =
    itemsOk && !itemsRes.error
      ? ((itemsRes.data ?? []) as Record<string, unknown>[])
          .map(mapItemRow)
          .filter((r): r is MemberItemInstance => r != null)
      : [];
  const assignmentPoints = asgnOk
    ? ((asgnRes.data ?? []) as Record<string, unknown>[])
        .map(mapAssignmentPointRow)
        .filter((r): r is MemberAssignmentPoint => r != null)
        .map((r) => (r.groupId === groupId ? r : { ...r, groupId }))
    : [];
  if (
    profileCardsRes.error &&
    !/does not exist|schema cache/i.test(profileCardsRes.error.message ?? '')
  ) {
    throw new Error(profileCardsRes.error.message);
  }
  const profileCardUnlocks = !profileCardsRes.error
    ? ((profileCardsRes.data ?? []) as Record<string, unknown>[])
        .map(mapProfileCardRow)
        .filter((r): r is MemberProfileCardUnlock => r != null)
    : [];

  return withAssignmentPointsOnGroup(
    {
      ...empty,
      records,
      coinRecords,
      notes,
      coinTotals: recomputeCoinTotals(coinRecords),
      itemInstances,
      rewardedLevels: {},
      profileCardUnlocks,
    },
    assignmentPoints
  );
}

/**
 * 그룹 단위 코인·아이템만 로드 (과제 보상 지급용).
 * 포인트/노트/과제체크 이력은 조회하지 않음.
 */
export async function loadMemberCoinAndItemsForGroup(
  workspaceId: string,
  gradeId: string,
  groupId: string
): Promise<GroupMemberPoints> {
  const empty = emptyGroupMemberPoints();
  if (!workspaceId || !gradeId || !groupId) return empty;

  const memberIds = await listMemberIdsForGroup(workspaceId, gradeId, groupId);
  if (memberIds.length === 0) return empty;

  const [coinsRes, itemsRes] = await Promise.all([
    supabase
      .from('class_member_coin_records')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('grade_id', gradeId)
      .in('class_member_id', memberIds)
      .order('created_at', { ascending: false }),
    supabase
      .from('class_member_item_instances')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('grade_id', gradeId)
      .in('class_member_id', memberIds)
      .order('acquired_at', { ascending: false }),
  ]);

  if (coinsRes.error && !/does not exist|schema cache/i.test(coinsRes.error.message ?? '')) {
    throw new Error(coinsRes.error.message);
  }
  const itemsOk =
    !itemsRes.error || !/does not exist|schema cache|is_locked/i.test(itemsRes.error.message ?? '');

  const coinRecords = ((coinsRes.data ?? []) as Record<string, unknown>[])
    .map(mapCoinRow)
    .filter((r): r is MemberCoinRecord => r != null);
  const itemInstances = itemsOk
    ? ((itemsRes.data ?? []) as Record<string, unknown>[])
        .map(mapItemRow)
        .filter((r): r is MemberItemInstance => r != null)
    : [];

  return {
    ...empty,
    coinRecords,
    coinTotals: recomputeCoinTotals(coinRecords),
    itemInstances,
  };
}

/**
 * 지정 멤버들의 코인·아이템만 교체 저장 (다른 그룹/멤버 데이터 유지).
 */
export async function replaceMemberCoinAndItemsForMembers(
  workspaceId: string,
  gradeId: string,
  memberIds: string[],
  group: GroupMemberPoints
): Promise<void> {
  if (!workspaceId || !gradeId || memberIds.length === 0) return;
  const idSet = new Set(memberIds);

  const coinRows = (group.coinRecords ?? [])
    .filter((r) => r.id && r.memberId && idSet.has(r.memberId))
    .map((r) => ({
      id: r.id,
      workspace_id: workspaceId,
      grade_id: gradeId,
      class_member_id: r.memberId,
      coin_value: r.coinValue ?? 0,
      coin_type: r.coinType ?? 'General',
      memo: r.memo ?? null,
      recorded_date: r.recordedDate || todayIso(),
      time_tag_label: r.timeTagLabel ?? null,
      created_at: r.createdAt || new Date().toISOString(),
    }));

  const itemRows = (group.itemInstances ?? [])
    .filter((r) => r.id && r.memberId && r.itemCardId && idSet.has(r.memberId))
    .map((r) => ({
      id: r.id,
      workspace_id: workspaceId,
      grade_id: gradeId,
      class_member_id: r.memberId,
      item_card_id: r.itemCardId,
      category: r.category ?? 'levelup',
      acquired_level: r.acquiredLevel ?? null,
      remaining_use_count:
        typeof r.remainingUseCount === 'number' ? r.remainingUseCount : null,
      usage_history: (r.usageHistory ?? []).map((h) => ({
        usedAt: h.usedAt,
        ...(h.memo ? { memo: h.memo } : {}),
      })),
      is_locked: r.isLocked === true,
      acquired_at: r.acquiredAt || new Date().toISOString(),
      created_at: r.acquiredAt || new Date().toISOString(),
    }));

  await upsertRowsThenPruneMembers(
    'class_member_coin_records',
    workspaceId,
    gradeId,
    memberIds,
    coinRows,
    { softMissing: true }
  );
  await upsertRowsThenPruneMembers(
    'class_member_item_instances',
    workspaceId,
    gradeId,
    memberIds,
    itemRows,
    { softMissing: true }
  );
}

async function insertItemInstanceRows(itemRows: Array<Record<string, unknown>>): Promise<void> {
  const { error } = await supabase.from('class_member_item_instances').insert(itemRows);
  if (!error) return;
  if (/usage_history/i.test(error.message ?? '')) {
    const stripped = itemRows.map(({ usage_history: _uh, ...rest }) => rest);
    const retry = await supabase.from('class_member_item_instances').insert(stripped);
    if (retry.error && !/does not exist|schema cache|is_locked/i.test(retry.error.message ?? '')) {
      throw new Error(retry.error.message);
    }
    return;
  }
  if (!/does not exist|schema cache|is_locked/i.test(error.message ?? '')) {
    throw new Error(error.message);
  }
}

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function buildPointCoinNoteItemRows(
  workspaceId: string,
  gradeId: string,
  groups: GroupMemberPoints[],
  memberIdSet?: Set<string>
): {
  pointRows: Array<Record<string, unknown>>;
  coinRows: Array<Record<string, unknown>>;
  noteRows: Array<Record<string, unknown>>;
  itemRows: Array<Record<string, unknown>>;
  profileCardRows: Array<Record<string, unknown>>;
} {
  const pointRows: Array<Record<string, unknown>> = [];
  const coinRows: Array<Record<string, unknown>> = [];
  const noteRows: Array<Record<string, unknown>> = [];
  const itemRows: Array<Record<string, unknown>> = [];
  const profileCardRows: Array<Record<string, unknown>> = [];
  const allow = (memberId: string) => !memberIdSet || memberIdSet.has(memberId);

  groups.forEach((g) => {
    (g.records ?? []).forEach((r) => {
      if (!r.id || !r.memberId || !allow(r.memberId)) return;
      pointRows.push({
        id: r.id,
        workspace_id: workspaceId,
        grade_id: gradeId,
        class_member_id: r.memberId,
        point_type_id: r.pointTypeId ?? '',
        point_value: r.pointValue ?? 0,
        point_memo: r.pointMemo ?? '',
        point_category: r.pointCategory === 'negative' ? 'negative' : 'positive',
        recorded_date: r.recordedDate || todayIso(),
        time_tag_label: r.timeTagLabel ?? null,
        created_at: r.createdAt || new Date().toISOString(),
      });
    });
    (g.coinRecords ?? []).forEach((r) => {
      if (!r.id || !r.memberId || !allow(r.memberId)) return;
      coinRows.push({
        id: r.id,
        workspace_id: workspaceId,
        grade_id: gradeId,
        class_member_id: r.memberId,
        coin_value: r.coinValue ?? 0,
        coin_type: r.coinType ?? 'General',
        memo: r.memo ?? null,
        recorded_date: r.recordedDate || todayIso(),
        time_tag_label: r.timeTagLabel ?? null,
        created_at: r.createdAt || new Date().toISOString(),
      });
    });
    (g.notes ?? []).forEach((r) => {
      if (!r.id || !r.memberId || !allow(r.memberId)) return;
      noteRows.push({
        id: r.id,
        workspace_id: workspaceId,
        grade_id: gradeId,
        class_member_id: r.memberId,
        text: r.text ?? '',
        note_type: r.noteType === '학습' || r.noteType === '행동' ? r.noteType : '일반',
        recorded_date: r.recordedDate || todayIso(),
        time_tag_label: r.timeTagLabel ?? null,
        created_at: r.createdAt || new Date().toISOString(),
      });
    });
    (g.itemInstances ?? []).forEach((r) => {
      if (!r.id || !r.memberId || !r.itemCardId || !allow(r.memberId)) return;
      itemRows.push({
        id: r.id,
        workspace_id: workspaceId,
        grade_id: gradeId,
        class_member_id: r.memberId,
        item_card_id: r.itemCardId,
        category: r.category ?? 'levelup',
        acquired_level: r.acquiredLevel ?? null,
        remaining_use_count:
          typeof r.remainingUseCount === 'number' ? r.remainingUseCount : null,
        usage_history: (r.usageHistory ?? []).map((h) => ({
          usedAt: h.usedAt,
          ...(h.memo ? { memo: h.memo } : {}),
        })),
        is_locked: r.isLocked === true,
        acquired_at: r.acquiredAt || new Date().toISOString(),
        created_at: r.acquiredAt || new Date().toISOString(),
      });
    });
    (g.profileCardUnlocks ?? []).forEach((r) => {
      if (!r.id || !r.memberId || !r.setId || !allow(r.memberId)) return;
      profileCardRows.push({
        id: r.id,
        workspace_id: workspaceId,
        grade_id: gradeId,
        class_member_id: r.memberId,
        set_id: r.setId,
        slot_index: r.slotIndex,
        source_kind: r.sourceKind === 'workspace' ? 'workspace' : 'admin',
        quantity: Math.max(1, r.quantity ?? 1),
        unlocked_at: r.unlockedAt || new Date().toISOString(),
      });
    });
  });

  return { pointRows, coinRows, noteRows, itemRows, profileCardRows };
}

/**
 * 프로필 카드는 (member, source, set, slot) 유니크인데 클라이언트는 뽑을 때마다
 * 새 id를 만들 수 있다. upsert(onConflict: id)만 쓰면 유니크 충돌이 나므로
 * DB에 이미 있는 행 id로 맞추고, 동일 키 중복 행은 수량 큰 쪽으로 합친다.
 */
async function reconcileProfileCardRowsForUpsert(
  workspaceId: string,
  gradeId: string,
  memberIds: string[],
  rows: Array<Record<string, unknown>>
): Promise<Array<Record<string, unknown>>> {
  if (rows.length === 0) return rows;
  const { data, error } = await supabase
    .from('class_member_profile_cards')
    .select('id, class_member_id, source_kind, set_id, slot_index')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId)
    .in('class_member_id', memberIds);
  if (error) {
    if (/does not exist|schema cache/i.test(error.message ?? '')) return rows;
    throw new Error(error.message);
  }
  const keyOf = (r: Record<string, unknown>) =>
    `${String(r.class_member_id)}|${String(r.source_kind ?? 'admin')}|${String(r.set_id)}|${Number(r.slot_index) || 0}`;
  const existingIdByKey = new Map<string, string>();
  for (const raw of data ?? []) {
    const r = raw as Record<string, unknown>;
    const id = typeof r.id === 'string' ? r.id : '';
    if (id) existingIdByKey.set(keyOf(r), id);
  }
  const merged = new Map<string, Record<string, unknown>>();
  for (const row of rows) {
    const k = keyOf(row);
    const prev = merged.get(k);
    if (!prev) {
      merged.set(k, { ...row });
      continue;
    }
    const prevQty = Math.max(1, Number(prev.quantity) || 1);
    const nextQty = Math.max(1, Number(row.quantity) || 1);
    if (nextQty >= prevQty) merged.set(k, { ...row });
  }
  return Array.from(merged.values()).map((row) => {
    const existingId = existingIdByKey.get(keyOf(row));
    return existingId ? { ...row, id: existingId } : row;
  });
}

async function upsertRowsThenPruneMembers(
  table: string,
  workspaceId: string,
  gradeId: string,
  memberIds: string[],
  rows: Array<Record<string, unknown>>,
  options?: { softMissing?: boolean }
): Promise<void> {
  let effectiveRows = rows;
  if (table === 'class_member_profile_cards' && rows.length > 0) {
    effectiveRows = await reconcileProfileCardRowsForUpsert(
      workspaceId,
      gradeId,
      memberIds,
      rows
    );
  }

  if (effectiveRows.length > 0) {
    // class_member_notes.text는 암호화 컬럼이라 직접 upsert 대신 RPC(SECURITY DEFINER)로 암호화해 저장한다.
    let { error } =
      table === 'class_member_notes'
        ? await supabase.rpc('class_member_notes_upsert', { p_rows: effectiveRows })
        : await supabase.from(table).upsert(effectiveRows, { onConflict: 'id' });
    if (error && /usage_history/i.test(error.message ?? '')) {
      const stripped = effectiveRows.map(({ usage_history: _uh, ...rest }) => rest);
      const retry = await supabase.from(table).upsert(stripped, { onConflict: 'id' });
      error = retry.error;
    }
    if (error) {
      if (
        options?.softMissing &&
        /does not exist|schema cache|is_locked/i.test(error.message ?? '')
      ) {
        return;
      }
      if (/class_member_profile_cards_uq|duplicate key/i.test(error.message ?? '')) {
        throw new Error(
          '프로필 카드 보유 정보가 충돌했습니다. 잠시 후 다시 시도해 주세요.'
        );
      }
      throw new Error(error.message);
    }
  }

  // 텍스트 id의 `.not('id','in',...)` 는 방금 저장한 행까지 지울 수 있어
  // 기존 id를 조회한 뒤 차집합만 `.in('id', …)` 로 삭제한다.
  const { data: existing, error: selErr } = await supabase
    .from(table)
    .select('id')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId)
    .in('class_member_id', memberIds);
  if (selErr) {
    if (
      options?.softMissing &&
      /does not exist|schema cache|is_locked/i.test(selErr.message ?? '')
    ) {
      return;
    }
    throw new Error(selErr.message);
  }

  const keepSet = new Set(effectiveRows.map((r) => String(r.id ?? '')).filter(Boolean));
  const toDelete = ((existing ?? []) as Array<{ id?: string }>)
    .map((r) => r.id)
    .filter((id): id is string => typeof id === 'string' && !!id && !keepSet.has(id));

  const chunkSize = 100;
  for (let i = 0; i < toDelete.length; i += chunkSize) {
    const chunk = toDelete.slice(i, i + chunkSize);
    const { error: delErr } = await supabase.from(table).delete().in('id', chunk);
    if (delErr) {
      if (
        options?.softMissing &&
        /does not exist|schema cache|is_locked/i.test(delErr.message ?? '')
      ) {
        return;
      }
      throw new Error(delErr.message);
    }
  }
}

/**
 * 지정 멤버들의 포인트·코인·노트·아이템만 교체 저장 (다른 그룹/멤버 유지).
 * upsert 후 고아 행만 삭제 — DELETE→INSERT 실패로 데이터가 통째로 날아가는 것 방지.
 */
export async function replaceMemberPointDataForMembers(
  workspaceId: string,
  gradeId: string,
  memberIds: string[],
  group: GroupMemberPoints
): Promise<void> {
  if (!workspaceId || !gradeId || memberIds.length === 0) return;
  const idSet = new Set(memberIds);
  const { pointRows, coinRows, noteRows, itemRows, profileCardRows } = buildPointCoinNoteItemRows(
    workspaceId,
    gradeId,
    [group],
    idSet
  );

  await upsertRowsThenPruneMembers(
    'class_member_point_records',
    workspaceId,
    gradeId,
    memberIds,
    pointRows
  );
  await upsertRowsThenPruneMembers(
    'class_member_coin_records',
    workspaceId,
    gradeId,
    memberIds,
    coinRows,
    { softMissing: true }
  );
  await upsertRowsThenPruneMembers(
    'class_member_notes',
    workspaceId,
    gradeId,
    memberIds,
    noteRows,
    { softMissing: true }
  );

  await upsertRowsThenPruneMembers(
    'class_member_item_instances',
    workspaceId,
    gradeId,
    memberIds,
    itemRows,
    { softMissing: true }
  );

  await upsertRowsThenPruneMembers(
    'class_member_profile_cards',
    workspaceId,
    gradeId,
    memberIds,
    profileCardRows,
    { softMissing: true }
  );
}

/** @deprecated 학년 전체 교체 — 부분 로드 시 타 그룹 데이터 삭제 위험. 가급적 replaceMemberPointDataForMembers 사용 */
export async function replaceMemberPointDataForGrade(
  workspaceId: string,
  gradeId: string,
  memberPointsByGroup: Record<string, GroupMemberPoints>
): Promise<void> {
  if (!workspaceId || !gradeId) return;

  const { pointRows, coinRows, noteRows, itemRows } = buildPointCoinNoteItemRows(
    workspaceId,
    gradeId,
    Object.values(memberPointsByGroup)
  );

  const delPoint = await supabase
    .from('class_member_point_records')
    .delete()
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId);
  if (delPoint.error) {
    if (/does not exist|schema cache/i.test(delPoint.error.message ?? '')) return;
    throw new Error(delPoint.error.message);
  }
  const delCoin = await supabase
    .from('class_member_coin_records')
    .delete()
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId);
  if (delCoin.error) throw new Error(delCoin.error.message);
  const delNotes = await supabase
    .from('class_member_notes')
    .delete()
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId);
  if (delNotes.error) throw new Error(delNotes.error.message);

  if (pointRows.length > 0) {
    const { error } = await supabase.from('class_member_point_records').insert(pointRows);
    if (error) throw new Error(error.message);
  }
  if (coinRows.length > 0) {
    const { error } = await supabase.from('class_member_coin_records').insert(coinRows);
    if (error) throw new Error(error.message);
  }
  if (noteRows.length > 0) {
    // class_member_notes.text는 암호화 컬럼이라 RPC로 암호화해 저장한다.
    const { error } = await supabase.rpc('class_member_notes_upsert', { p_rows: noteRows });
    if (error) throw new Error(error.message);
  }

  const delItems = await supabase
    .from('class_member_item_instances')
    .delete()
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId);
  if (delItems.error) {
    if (!/does not exist|schema cache|is_locked/i.test(delItems.error.message ?? '')) {
      throw new Error(delItems.error.message);
    }
  } else if (itemRows.length > 0) {
    await insertItemInstanceRows(itemRows);
  }
}

function mapAssignmentPointKind(raw: unknown): AssignmentPointKind {
  if (raw === 'project' || raw === 'routine') return raw;
  return 'general';
}

function mapAssignmentPointStatus(raw: unknown): AssignmentPointStatus {
  if (
    raw === 'confirmed' ||
    raw === 'delayed' ||
    raw === 'exempted' ||
    raw === 'excluded' ||
    raw === 'submitted'
  )
    return raw;
  return 'pending';
}

function mapAssignmentPointRow(row: Record<string, unknown>): MemberAssignmentPoint | null {
  const id = typeof row.id === 'string' ? row.id : '';
  const memberId = typeof row.class_member_id === 'string' ? row.class_member_id : '';
  if (!id || !memberId) return null;
  const earnedItemIds = Array.isArray(row.earned_item_ids)
    ? (row.earned_item_ids as unknown[]).filter((x): x is string => typeof x === 'string')
    : [];
  return {
    id,
    memberId,
    groupId: typeof row.group_id === 'string' ? row.group_id : '',
    assignmentItemId: typeof row.assignment_item_id === 'string' ? row.assignment_item_id : '',
    assignmentSubId: typeof row.assignment_sub_id === 'string' ? row.assignment_sub_id : undefined,
    assignmentKind: mapAssignmentPointKind(row.assignment_kind),
    assignmentTitle: typeof row.assignment_title === 'string' ? row.assignment_title : '',
    status: mapAssignmentPointStatus(row.status),
    earnedPoints: Number(row.earned_points) || 0,
    earnedCoins: Number(row.earned_coins) || 0,
    earnedItemIds,
    recordedDate: typeof row.recorded_date === 'string' ? row.recorded_date : '',
    memo: typeof row.memo === 'string' ? row.memo : undefined,
    createdAt: typeof row.created_at === 'string' ? row.created_at : new Date().toISOString(),
    updatedAt: typeof row.updated_at === 'string' ? row.updated_at : new Date().toISOString(),
  };
}

function recomputeAssignmentTotals(records: MemberAssignmentPoint[]): Record<string, number> {
  const totals: Record<string, number> = {};
  records.forEach((r) => {
    if (r.status === 'excluded') return;
    totals[r.memberId] = (totals[r.memberId] ?? 0) + r.earnedPoints;
  });
  return totals;
}

/** 행동 포인트(records) 합 */
export function memberBehaviorPointTotal(
  group: GroupMemberPoints,
  memberId: string
): number {
  let behavior = 0;
  for (const r of group.records ?? []) {
    if (r.memberId === memberId) behavior += r.pointValue;
  }
  return behavior;
}

/** 단일 그룹 기준: 행동 + 과제 합산 */
export function memberCombinedPointTotal(
  group: GroupMemberPoints,
  memberId: string
): number {
  const behavior = memberBehaviorPointTotal(group, memberId);
  const list = group.assignmentPoints ?? [];
  if (list.length > 0) {
    let asgn = 0;
    for (const r of list) {
      if (r.memberId === memberId && r.status !== 'excluded') {
        asgn += r.earnedPoints || 0;
      }
    }
    return behavior + asgn;
  }
  return behavior + (group.assignmentTotals?.[memberId] ?? 0);
}

/**
 * 문서 전체에서 멤버의 과제 포인트 합 (그룹 키 불일치 대비).
 * 학생 블럭은 소속 그룹에 그려지므로, 과제 행의 group_id와 달라도 멤버 기준으로 집계한다.
 */
export function memberAssignmentPointTotalFromDoc(
  memberPointsByGroup: Record<string, GroupMemberPoints>,
  memberId: string
): number {
  let asgn = 0;
  const seen = new Set<string>();
  for (const g of Object.values(memberPointsByGroup)) {
    for (const r of g.assignmentPoints ?? []) {
      if (r.memberId !== memberId || r.status === 'excluded') continue;
      if (seen.has(r.id)) continue;
      seen.add(r.id);
      asgn += r.earnedPoints || 0;
    }
  }
  if (seen.size > 0) return asgn;
  for (const g of Object.values(memberPointsByGroup)) {
    asgn += g.assignmentTotals?.[memberId] ?? 0;
  }
  return asgn;
}

export function memberDisplayPointTotal(
  memberPointsByGroup: Record<string, GroupMemberPoints>,
  group: GroupMemberPoints,
  memberId: string
): number {
  return (
    memberBehaviorPointTotal(group, memberId) +
    memberAssignmentPointTotalFromDoc(memberPointsByGroup, memberId)
  );
}

/** 그룹 데이터에 과제 이력을 넣고 totals = 행동 + 과제로 재계산 */
export function withAssignmentPointsOnGroup(
  group: GroupMemberPoints,
  assignmentPoints: MemberAssignmentPoint[]
): GroupMemberPoints {
  const assignmentTotals = recomputeAssignmentTotals(assignmentPoints);
  const behaviorTotals = recomputeTotals(group.records ?? []);
  const totals: Record<string, number> = { ...behaviorTotals };
  Object.entries(assignmentTotals).forEach(([mid, pts]) => {
    totals[mid] = (totals[mid] ?? 0) + pts;
  });
  return {
    ...group,
    assignmentPoints,
    assignmentTotals,
    totals,
  };
}

/**
 * 학년 과제 포인트를 그룹별로 로드.
 * 집계 키는 멤버의 현재 소속 그룹(class_members)을 우선 — 학생 블럭 표시와 일치.
 */
export async function loadAssignmentPointsByGroup(
  workspaceId: string,
  gradeId: string
): Promise<Record<string, MemberAssignmentPoint[]>> {
  if (!workspaceId || !gradeId) return {};

  const [asgnRes, membersRes] = await Promise.all([
    supabase
      .from('class_member_assignment_rewards')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('grade_id', gradeId)
      .order('recorded_date', { ascending: false }),
    supabase
      .from('class_members')
      .select('id, member_group_id')
      .eq('workspace_id', workspaceId)
      .eq('grade_id', gradeId),
  ]);

  if (asgnRes.error) {
    if (/does not exist|schema cache/i.test(asgnRes.error.message ?? '')) return {};
    throw new Error(asgnRes.error.message);
  }

  const memberGroup = new Map<string, string>();
  ((membersRes.data ?? []) as Array<{ id: string; member_group_id: string | null }>).forEach(
    (m) => {
      if (m.member_group_id) memberGroup.set(m.id, m.member_group_id);
    }
  );

  const byGroup: Record<string, MemberAssignmentPoint[]> = {};
  ((asgnRes.data ?? []) as Record<string, unknown>[]).forEach((row) => {
    const mapped = mapAssignmentPointRow(row);
    if (!mapped) return;
    const gid = memberGroup.get(mapped.memberId) || mapped.groupId;
    if (!gid) return;
    const normalized =
      mapped.groupId === gid ? mapped : { ...mapped, groupId: gid };
    if (!byGroup[gid]) byGroup[gid] = [];
    byGroup[gid].push(normalized);
  });
  return byGroup;
}

/**
 * 문서에 과제 포인트를 병합하고 totals = 행동 + 과제 로 갱신.
 * 조회 결과가 비어 있으면 기존 assignmentPoints를 지우지 않는다(와이프 방지).
 */
export function mergeAssignmentPointsIntoDoc(
  doc: PointItemNoteDoc,
  asgnByGroup: Record<string, MemberAssignmentPoint[]>
): PointItemNoteDoc {
  const incomingCount = Object.values(asgnByGroup).reduce((n, arr) => n + arr.length, 0);
  if (incomingCount === 0) {
    // 기존 과제 이력이 있으면 totals만 재합산
    const nextByGroup: Record<string, GroupMemberPoints> = {};
    let hadAsgn = false;
    Object.entries(doc.memberPointsByGroup).forEach(([gid, g]) => {
      if ((g.assignmentPoints?.length ?? 0) > 0 || Object.keys(g.assignmentTotals ?? {}).length > 0) {
        hadAsgn = true;
        nextByGroup[gid] = withAssignmentPointsOnGroup(g, g.assignmentPoints ?? []);
      } else {
        nextByGroup[gid] = g;
      }
    });
    return hadAsgn ? { ...doc, memberPointsByGroup: nextByGroup } : doc;
  }

  const nextByGroup: Record<string, GroupMemberPoints> = { ...doc.memberPointsByGroup };
  // 기존 그룹의 과제 이력을 비운 뒤, 멤버 소속 기준으로 다시 채움
  Object.keys(nextByGroup).forEach((gid) => {
    const g = nextByGroup[gid];
    nextByGroup[gid] = {
      ...g,
      assignmentPoints: [],
      assignmentTotals: {},
    };
  });
  Object.entries(asgnByGroup).forEach(([gid, asgn]) => {
    const g = nextByGroup[gid] ?? emptyGroupMemberPoints();
    nextByGroup[gid] = withAssignmentPointsOnGroup(g, asgn);
  });
  // 과제 없는 그룹도 행동만으로 totals 재계산
  Object.keys(nextByGroup).forEach((gid) => {
    if (asgnByGroup[gid]) return;
    const g = nextByGroup[gid];
    nextByGroup[gid] = withAssignmentPointsOnGroup(g, []);
  });
  return { ...doc, memberPointsByGroup: nextByGroup };
}

/** 그룹별 과제 포인트 이력 로드 */
export async function loadMemberAssignmentPointsForGroup(
  workspaceId: string,
  gradeId: string,
  groupId: string
): Promise<MemberAssignmentPoint[]> {
  if (!workspaceId || !gradeId || !groupId) return [];

  const { data, error } = await supabase
    .from('class_member_assignment_rewards')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId)
    .eq('group_id', groupId)
    .order('recorded_date', { ascending: false });

  if (error) {
    if (/does not exist|schema cache/i.test(error.message ?? '')) return [];
    throw new Error(error.message);
  }

  return ((data ?? []) as Record<string, unknown>[])
    .map(mapAssignmentPointRow)
    .filter((r): r is MemberAssignmentPoint => r != null);
}

/** 멤버별 과제 포인트 이력 로드 (이력 모달용) */
export async function loadMemberAssignmentPointsForMember(
  workspaceId: string,
  gradeId: string,
  memberId: string
): Promise<MemberAssignmentPoint[]> {
  if (!workspaceId || !gradeId || !memberId) return [];
  return loadMemberAssignmentPointsForMembers(workspaceId, gradeId, [memberId]);
}

/** 여러 멤버의 과제 포인트 이력 로드 (학생 블럭 합산용 — group_id 무관) */
export async function loadMemberAssignmentPointsForMembers(
  workspaceId: string,
  gradeId: string,
  memberIds: string[]
): Promise<MemberAssignmentPoint[]> {
  const ids = Array.from(new Set(memberIds.filter(Boolean)));
  if (!workspaceId || !gradeId || ids.length === 0) return [];

  const out: MemberAssignmentPoint[] = [];
  const chunkSize = 100;
  for (let i = 0; i < ids.length; i += chunkSize) {
    const chunk = ids.slice(i, i + chunkSize);
    const { data, error } = await supabase
      .from('class_member_assignment_rewards')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('grade_id', gradeId)
      .in('class_member_id', chunk)
      .order('recorded_date', { ascending: false });

    if (error) {
      if (/does not exist|schema cache/i.test(error.message ?? '')) return out;
      throw new Error(error.message);
    }

    ((data ?? []) as Record<string, unknown>[]).forEach((row) => {
      const mapped = mapAssignmentPointRow(row);
      if (mapped) out.push(mapped);
    });
  }
  return out;
}

/**
 * 그룹 멤버 기준으로 과제 포인트를 다시 조회해 합산 반영.
 * (캐시에 빈 assignmentPoints가 고정돼도 DB 값을 복구)
 */
export async function withFreshAssignmentPointsForGroup(
  workspaceId: string,
  gradeId: string,
  groupId: string,
  group: GroupMemberPoints
): Promise<GroupMemberPoints> {
  const memberIds = await listMemberIdsForGroup(workspaceId, gradeId, groupId);
  if (memberIds.length === 0) {
    return withAssignmentPointsOnGroup(group, []);
  }
  const asgn = await loadMemberAssignmentPointsForMembers(
    workspaceId,
    gradeId,
    memberIds
  );
  const tagged = asgn.map((r) =>
    r.groupId === groupId ? r : { ...r, groupId }
  );
  return withAssignmentPointsOnGroup(group, tagged);
}

export type AssignmentPointSyncInput = {
  memberId: string;
  groupId: string;
  assignmentItemId: string;
  assignmentSubId?: string;
  assignmentKind: AssignmentPointKind;
  assignmentTitle: string;
  status: AssignmentPointStatus;
  earnedPoints: number;
  earnedCoins?: number;
  earnedItemIds?: string[];
  recordedDate: string;
  memo?: string;
};

export type SyncAssignmentPointsOpts = {
  /** 문서에 없는 과제 행 전체 삭제(학년 단위 orphan 정리) */
  allAssignmentItemIds?: string[];
  /**
   * 그룹 단위 교체: 해당 과제+그룹에서 이번 upsert id에 없는 행 삭제.
   * (다른 그룹 데이터는 유지)
   */
  replaceScope?: { assignmentItemId: string; groupId: string };
  /**
   * 과제 단위 교체: 해당 과제의 모든 그룹에서 이번 upsert id에 없는 행 삭제.
   * (하위과제·루틴 날짜 구조 변경)
   */
  replaceAssignmentItemIds?: string[];
};

/** 과제 체크 결과를 테이블에 동기화 (upsert + delete) */
export async function syncAssignmentPointsToTable(
  workspaceId: string,
  gradeId: string,
  inputs: AssignmentPointSyncInput[],
  /** @deprecated allAssignmentItemIds — opts.allAssignmentItemIds 사용 권장 */
  allAssignmentItemIdsOrOpts?: string[] | SyncAssignmentPointsOpts
): Promise<void> {
  if (!workspaceId || !gradeId) return;

  const opts: SyncAssignmentPointsOpts = Array.isArray(allAssignmentItemIdsOrOpts)
    ? { allAssignmentItemIds: allAssignmentItemIdsOrOpts }
    : allAssignmentItemIdsOrOpts ?? {};

  const now = new Date().toISOString();
  const rowIds: string[] = [];

  // 1) upsert
  if (inputs.length > 0) {
    const rows = inputs.map((input) => {
      const subId = input.assignmentSubId ?? '';
      const id = `asgn-${input.assignmentItemId}-${subId}-${input.memberId}`.replace(/--/g, '-');
      rowIds.push(id);
      return {
        id,
        workspace_id: workspaceId,
        grade_id: gradeId,
        group_id: input.groupId,
        class_member_id: input.memberId,
        assignment_item_id: input.assignmentItemId,
        assignment_sub_id: subId || null,
        assignment_kind: input.assignmentKind,
        assignment_title: input.assignmentTitle,
        status: input.status,
        earned_points: input.earnedPoints,
        earned_coins: input.earnedCoins ?? 0,
        earned_item_ids: input.earnedItemIds ?? [],
        recorded_date: input.recordedDate || todayIso(),
        memo: input.memo ?? null,
        updated_at: now,
      };
    });

    const { error } = await supabase
      .from('class_member_assignment_rewards')
      .upsert(rows, { onConflict: 'id' });

    if (error && !/does not exist|schema cache/i.test(error.message ?? '')) {
      throw new Error(error.message);
    }
  }

  // 2a) 과제 단위(전 그룹): 해당 과제에서 이번 동기화에 없는 행 삭제
  if (opts.replaceAssignmentItemIds && opts.replaceAssignmentItemIds.length > 0) {
    const keepSet = new Set(rowIds);
    const { data: existing, error: selErr } = await supabase
      .from('class_member_assignment_rewards')
      .select('id')
      .eq('workspace_id', workspaceId)
      .eq('grade_id', gradeId)
      .in('assignment_item_id', opts.replaceAssignmentItemIds);
    if (selErr && !/does not exist|schema cache/i.test(selErr.message ?? '')) {
      throw new Error(selErr.message);
    }
    const toDelete = ((existing ?? []) as Array<{ id?: string }>)
      .map((r) => r.id)
      .filter((id): id is string => typeof id === 'string' && !!id && !keepSet.has(id));
    const chunkSize = 100;
    for (let i = 0; i < toDelete.length; i += chunkSize) {
      const chunk = toDelete.slice(i, i + chunkSize);
      const { error: scopeDelErr } = await supabase
        .from('class_member_assignment_rewards')
        .delete()
        .in('id', chunk);
      if (scopeDelErr && !/does not exist|schema cache/i.test(scopeDelErr.message ?? '')) {
        throw new Error(scopeDelErr.message);
      }
    }
  }

  // 2b) 그룹 스코프: 해당 과제·그룹에서 이번 동기화에 없는 행만 삭제
  // (텍스트 id의 `.not('id','in',…)` 는 upsert한 행까지 지울 수 있어 조회 후 차집합 삭제)
  if (!opts.replaceAssignmentItemIds?.length && opts.replaceScope) {
    const { assignmentItemId, groupId } = opts.replaceScope;
    const { data: existing, error: selErr } = await supabase
      .from('class_member_assignment_rewards')
      .select('id')
      .eq('workspace_id', workspaceId)
      .eq('grade_id', gradeId)
      .eq('assignment_item_id', assignmentItemId)
      .eq('group_id', groupId);
    if (selErr && !/does not exist|schema cache/i.test(selErr.message ?? '')) {
      throw new Error(selErr.message);
    }
    const keepSet = new Set(rowIds);
    const toDelete = ((existing ?? []) as Array<{ id?: string }>)
      .map((r) => r.id)
      .filter((id): id is string => typeof id === 'string' && !!id && !keepSet.has(id));
    const chunkSize = 100;
    for (let i = 0; i < toDelete.length; i += chunkSize) {
      const chunk = toDelete.slice(i, i + chunkSize);
      const { error: scopeDelErr } = await supabase
        .from('class_member_assignment_rewards')
        .delete()
        .in('id', chunk);
      if (scopeDelErr && !/does not exist|schema cache/i.test(scopeDelErr.message ?? '')) {
        throw new Error(scopeDelErr.message);
      }
    }
  }

  // 2c) 학년 단위 orphan: 문서에서 제거된 과제 행 삭제
  if (opts.allAssignmentItemIds && opts.allAssignmentItemIds.length > 0) {
    const keepItems = new Set(opts.allAssignmentItemIds);
    const { data: existing, error: selErr } = await supabase
      .from('class_member_assignment_rewards')
      .select('id, assignment_item_id')
      .eq('workspace_id', workspaceId)
      .eq('grade_id', gradeId);
    if (selErr && !/does not exist|schema cache/i.test(selErr.message ?? '')) {
      throw new Error(selErr.message);
    }
    const toDelete = (
      (existing ?? []) as Array<{ id?: string; assignment_item_id?: string }>
    )
      .filter((r) => r.id && r.assignment_item_id && !keepItems.has(r.assignment_item_id))
      .map((r) => r.id as string);
    const chunkSize = 100;
    for (let i = 0; i < toDelete.length; i += chunkSize) {
      const chunk = toDelete.slice(i, i + chunkSize);
      const { error: delError } = await supabase
        .from('class_member_assignment_rewards')
        .delete()
        .in('id', chunk);
      if (delError && !/does not exist|schema cache/i.test(delError.message ?? '')) {
        throw new Error(delError.message);
      }
    }
  }
}

/** 특정 과제 삭제 시 관련 행 삭제 */
export async function deleteAssignmentRewardsForItem(
  workspaceId: string,
  gradeId: string,
  assignmentItemId: string
): Promise<void> {
  if (!workspaceId || !gradeId || !assignmentItemId) return;

  const { error } = await supabase
    .from('class_member_assignment_rewards')
    .delete()
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId)
    .eq('assignment_item_id', assignmentItemId);

  if (error && !/does not exist|schema cache/i.test(error.message ?? '')) {
    throw new Error(error.message);
  }
}

/** 멤버 삭제 시 과제 포인트 테이블에서도 제거 (CASCADE로 자동 처리되지만 명시적 호출용) */
export async function deleteAssignmentPointsForMembers(
  workspaceId: string,
  gradeId: string,
  memberIds: string[]
): Promise<void> {
  if (!workspaceId || !gradeId || memberIds.length === 0) return;

  const { error } = await supabase
    .from('class_member_assignment_rewards')
    .delete()
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId)
    .in('class_member_id', memberIds);

  if (error && !/does not exist|schema cache/i.test(error.message ?? '')) {
    throw new Error(error.message);
  }
}

export { recomputeAssignmentTotals };
